import { memo, useEffect, useMemo, useState } from 'react'
import { useFormState } from 'react-final-form'
import { Button } from '@cargomart/ui'
import { objectDiff } from 'helpers/diff'
import { useIsMobile } from 'hooks/media'
import { useTypedSelector } from 'hooks/useTypedSelector'
import debounce from 'lodash/debounce'
import { useGetDeliveryCostMutation } from 'modules/deliveryInfo/api'
import { DeliveryCostRequest } from 'modules/deliveryInfo/types'
import { OrderCargoInfo } from 'schema'

import { prepareCargo, prepareCargoTotal, prepareOperationsForDeliveryInfo } from '../../helpers/order'
import { DEBOUNCE_DELAY } from '../../pages/OrderCreateRoute/constants'
import {
  validateCargo,
  validateCargoTotal,
  validateCourierPoint,
  validateTerminalPoint
} from '../../pages/OrderCreateRoute/OrderCreateForm'
import { IOrderCreateFormData } from '../../types/orderGuestCreateTypes'
import { OrderOperationsBlock } from '../OrderOperationsBlock/OrderOperationsBlock'
import { OrderSummaryBlock } from '../OrderSummaryBlock'
import OrderGuestSubmit from './OrderGuestSubmit'

interface IProps {
  className?: string
  isShowPhone?: boolean
}

export const OrderGuestSummary = memo<IProps>(function OrderGuestSummary({ className, isShowPhone }) {
  const isMobile = useIsMobile()
  const { values } = useFormState<IOrderCreateFormData>()
  const { cargo, cargoTotal, cargoType } = values

  const [getDeliveryCost, { data: deliveryCost, isLoading: isDeliveryCostLoading, reset: resetDeliveryCost }] =
    useGetDeliveryCostMutation()

  const debouncedGetDeliveryCost = useMemo(() => debounce(getDeliveryCost, DEBOUNCE_DELAY), [getDeliveryCost])

  const { fromPoint, toPoint } = useTypedSelector((state) => state.orderAddReducer)

  const fromRes = useMemo(() => {
    const rawRes = {
      terminalId: fromPoint.selectedTerminal?.id,
      isCourier: fromPoint.isCourier,
      addressDetail: {
        latitude: fromPoint.isCourier ? fromPoint.selectedOption?.latitude : fromPoint.selectedTerminal?.latitude,
        longitude: fromPoint.isCourier ? fromPoint.selectedOption?.longitude : fromPoint.selectedTerminal?.longitude,
        fiasId: fromPoint.selectedOption?.fiasId
      }
    }
    if (rawRes.isCourier) {
      return rawRes
    }

    return {
      terminalId: rawRes.terminalId,
      isCourier: rawRes.isCourier
    }
  }, [fromPoint.selectedTerminal, fromPoint.isCourier, fromPoint.selectedOption])

  const fromResValid = useMemo(() => {
    if (fromPoint.errorAddress) {
      return false
    }
    if (fromRes.isCourier) {
      return validateCourierPoint(fromRes)
    }

    return validateTerminalPoint(fromRes)
  }, [fromRes, fromPoint])

  const toRes = useMemo(() => {
    const rawRes = {
      terminalId: toPoint.selectedTerminal?.id,
      isCourier: toPoint.isCourier,
      addressDetail: {
        latitude: toPoint.isCourier ? toPoint.selectedOption?.latitude : toPoint.selectedTerminal?.latitude,
        longitude: toPoint.isCourier ? toPoint.selectedOption?.longitude : toPoint.selectedTerminal?.longitude,
        fiasId: toPoint.selectedOption?.fiasId
      }
    }
    if (rawRes.isCourier) {
      return rawRes
    }

    return {
      terminalId: rawRes.terminalId,
      isCourier: rawRes.isCourier
    }
  }, [toPoint])

  const toResValid = useMemo(() => {
    if (toPoint.errorAddress) {
      return false
    }
    if (toRes.isCourier) {
      return validateCourierPoint(toRes)
    }

    return validateTerminalPoint(toRes)
  }, [toRes, toPoint])

  const cargoRes = useMemo(() => prepareCargo(cargo), [cargo])

  const cargoResValid = useMemo(() => (cargo ? validateCargo(cargoRes) : false), [cargoRes, cargo])

  const cargoTotalRes = useMemo(() => prepareCargoTotal(cargoTotal), [cargoTotal])

  const cargoTotalResValid = useMemo(
    () => (cargoTotal ? validateCargoTotal(cargoTotalRes) : false),
    [cargoTotalRes, cargoTotal]
  )

  const [lastDataForCalc, setLastDataForCalc] = useState<any[]>([])

  const cargoFullRes = useMemo(() => {
    if (cargoType === OrderCargoInfo.type.TOTAL) {
      return {
        cargoInfo: {
          type: cargoType,
          total: {
            type: cargoTotalRes?.type,
            weight: cargoTotalRes?.weight,
            volume: cargoTotalRes?.volume,
            amount: cargoTotalRes?.amount
          }
        }
      }
    }

    return {
      cargoInfo: {
        type: cargoType,
        separate: cargoRes.map((item) => ({
          amount: item.amount,
          length: item.length,
          width: item.width,
          type: item.type,
          height: item.height,
          weight: item.weight
        }))
      }
    }
  }, [cargoRes, cargoTotalRes, cargoType])

  const cargoFullResValid = useMemo(() => {
    if (cargoType === OrderCargoInfo.type.TOTAL) {
      return cargoTotalResValid
    }
    if (cargoType === OrderCargoInfo.type.SEPARATE) {
      return cargoResValid
    }
  }, [cargoResValid, cargoTotalResValid, cargoType])

  const preparedOperations = useMemo(
    () =>
      prepareOperationsForDeliveryInfo(
        values.operation || [],
        fromPoint.selectedTerminal?.id || '',
        toPoint.selectedTerminal?.id || ''
      ),
    [values.operation, fromPoint.selectedTerminal, toPoint.selectedTerminal]
  )

  useEffect(() => {
    if (
      !Object.keys(
        objectDiff(
          [
            toRes,
            fromRes,
            toResValid,
            fromResValid,
            debouncedGetDeliveryCost,
            cargoTotalResValid,
            cargoType,
            cargoFullResValid,
            cargoFullRes,
            preparedOperations
          ],
          lastDataForCalc
        )
      ).length
    ) {
      return
    }
    setLastDataForCalc([
      toRes,
      fromRes,
      toResValid,
      fromResValid,
      debouncedGetDeliveryCost,
      cargoTotalResValid,
      cargoType,
      cargoFullResValid,
      cargoFullRes,
      preparedOperations
    ])

    if (!cargoType || !fromResValid || !toResValid || !cargoFullResValid) {
      resetDeliveryCost()

      return
    }

    debouncedGetDeliveryCost({
      ...preparedOperations,
      departure: { ...preparedOperations.departure, point: fromRes },
      destination: { ...preparedOperations.destination, point: toRes },
      ...cargoFullRes
    } as unknown as DeliveryCostRequest)
    // eslint-disable-next-line
  }, [
    toRes,
    fromRes,
    toResValid,
    fromResValid,
    debouncedGetDeliveryCost,
    cargoTotalResValid,
    cargoType,
    cargoFullResValid,
    cargoFullRes,
    preparedOperations
  ])

  // TODO это костыль тк selectAddress не имеет валидации значения и проброса ошибки в форму
  const submitDisable = useMemo(() => Boolean(toPoint.errorAddress || fromPoint.errorAddress), [toPoint, fromPoint])

  return (
    <div className={className}>
      <OrderSummaryBlock
        cargoInfo={{
          // TODO исправить
          // @ts-ignore
          separate: prepareCargo(cargo),
          // TODO исправить
          // @ts-ignore
          total: prepareCargoTotal(cargoTotal),
          // TODO исправить
          // @ts-ignore
          type: cargoType
        }}
      />

      <OrderOperationsBlock
        className="mb-14 md:mb-0"
        departureTerminal={fromPoint.selectedTerminal}
        destinationTerminal={toPoint.selectedTerminal}
        isLoading={isDeliveryCostLoading}
        operations={deliveryCost?.operation || []}
        orderOperation={values?.operation}
        totalPrice={deliveryCost?.totalPrice}
      />

      {!isMobile && (
        <>
          {isShowPhone ? (
            <OrderGuestSubmit />
          ) : (
            <Button disabled={submitDisable} type="submit">
              Заказать
            </Button>
          )}
        </>
      )}
    </div>
  )
})
